import { createElement, useState } from "react";
import { ICustomTemplateAddEditDialogProps } from "./CustomTemplateAddEditDialog.tsx";
import { AnnualWheelTemplateModel, EntityTemplateSelection, RelativeTemplateDateConfigModel } from "./CustomTemplateAddEditDialog.types.ts";
import { useComplianceAreaQuery, useSelectableBusinessAreaQuery, useSelectableEntityTemplates } from "@common/hooks/useSelectableItemQueries";
import { isTemplateCustomer } from "../../../common/templateCustomerHelper.ts";
import { isNullOrWhitespace } from "../../../common/stringOperations.ts";
import { post, put } from "../../../common/api/apiShared.ts";
import { AnnualWheelActivitySubTask } from "../../../annualWheel/AnnualWheelSubTasks.types.ts";
import { getEnumValues } from "../../../common/enumOperations.ts";
import { AnnualWheelActivityGeneralType } from "../../../annualWheel/annualWheelDialog/AnnualWheelDialog.types.ts";
import { useTranslation } from "../../../localization/useTranslation.ts";
import { useDotLegalSnackbar } from "@dotlegal/dotlegal-ui-components";
import { Trans } from "react-i18next";
import { RecurringIntervalType } from "../../../annualWheel/AnnualWheel.types.ts";
import { useDotLegalMutation } from "@common/hooks/dotLegalReactQuery";
import { TemplateEntityType } from "../../../entityTemplates/EntityTemplate.types.ts";

export function useCustomTemplateAddEditDialog(props: ICustomTemplateAddEditDialogProps) {
    const snackBar = useDotLegalSnackbar();
    const [templateModel, setTemplateModel] = useState<AnnualWheelTemplateModel>(initiateTemplateModel());
    const [showError, setShowError] = useState(false);
    const { translateAnnualWheelActivityGeneralTypeEnum } = useTranslation();

    const complianceAreaQuery = useComplianceAreaQuery();
    const entityTemplatesQuery = useSelectableEntityTemplates(TemplateEntityType.Contract, props.isObligation === true);
    const businessAreaQuery = useSelectableBusinessAreaQuery(props.isObligation === true);

    const isTemplate = isTemplateCustomer();
    const isEdit = !isNullOrWhitespace(templateModel.id);

    const onSave = () => {
        if (templateModel.isInvalid()) {
            setShowError(true);
            return;
        }

        if (isEdit) {
            updateTemplate();
        } else {
            createTemplate();
        }
    };

    const createTemplate = async () => {
        await saveMutation.mutateAsync();
    };

    const updateTemplate = async () => {
        await updateMutation.mutateAsync();
    };

    function save() {
        return post<{}>("/annualWheelActivityTemplate", getSaveModel());
    }

    function update() {
        return put<{}>(`/annualWheelActivityTemplate/${templateModel.id}/`, getSaveModel());
    }

    const saveMutation = useDotLegalMutation(save, {
        onSuccess: (response: any) => {
            const value = response.value();
            const updatedModel = templateModel.withUpdates({ id: value.id });
            props.onCreated(updatedModel);
            snackBar.show(createElement(Trans, { i18nKey: "dataCreated", values: { name: templateModel.name } }));
            setTemplateModel(AnnualWheelTemplateModel.createDefaultTemplate());
        },
    });

    const updateMutation = useDotLegalMutation(update, {
        onSuccess: () => {
            props.onUpdated();
            setTemplateModel(AnnualWheelTemplateModel.createDefaultTemplate());
        },
    });

    const modelUpdater = {
        onNameChange: (name: string | undefined) => {
            const updatedModel = templateModel.withUpdates({ name });
            setTemplateModel(updatedModel);
        },
        onDescriptionChange: (description: string | undefined) => {
            const updatedModel = templateModel.withUpdates({ description });
            setTemplateModel(updatedModel);
        },
        onSubTaskChange: (subTasks: Array<AnnualWheelActivitySubTask>) => {
            const updatedModel = templateModel.withUpdates({ subTasks });
            setTemplateModel(updatedModel);
        },
        onIntervalChange: (interval: string) => {
            const updatedModel = templateModel.withUpdates({
                recurringInterval: isNullOrWhitespace(interval) ? undefined : Number(interval),
            });
            setTemplateModel(updatedModel);
        },
        onRecurringIntervalTypeChange: (type: string) => {
            const updatedModel = templateModel.withUpdates({ recurringIntervalType: Number(type) });
            setTemplateModel(updatedModel);
        },
        onNotificationPeriodInDaysChange: (notificationPeriodInDays: string) => {
            const updatedModel = templateModel.withUpdates({
                notificationPeriodInDays: isNullOrWhitespace(notificationPeriodInDays) ? undefined : Number(notificationPeriodInDays),
            });
            setTemplateModel(updatedModel);
        },
        onSingularActivityChange: (isSingleTimeActivity: boolean) => {
            const updatedModel = templateModel.withUpdates({
                recurringInterval: isSingleTimeActivity ? null : (props.templateModel?.recurringInterval ?? 12),
                recurringIntervalType: isSingleTimeActivity ? undefined : (props.templateModel?.recurringIntervalType ?? RecurringIntervalType.Month),
                endDateRelativeConfig: isSingleTimeActivity ? undefined : templateModel.endDateRelativeConfig,
            });
            setTemplateModel(updatedModel);
        },
        onPlanNowChange: (shouldPlanNow: boolean) => {
            const updatedModel = templateModel.withUpdates({
                planNow: shouldPlanNow,
            });
            setTemplateModel(updatedModel);
        },
        onBusinessAreasChange: (businessAreaIds: Array<string>) => {
            setTemplateModel(templateModel.withUpdates({ businessAreaIds }));
        },
        onTagsChange: (tagIds: Array<string>) => {
            setTemplateModel(templateModel.withUpdates({ tagIds }));
        },
        onComplianceAreasChange: (complianceAreas: Array<string>) => {
            const updatedModel = templateModel.withUpdates({ complianceAreaIds: complianceAreas });
            setTemplateModel(updatedModel);
        },
        onGeneralTypeChange: (generalType?: string | null) => {
            const updatedModel = templateModel.withUpdates({ generalType: generalType ? Number(generalType) : undefined });
            setTemplateModel(updatedModel);
        },
        onCustomIdChange: (customId: string) => {
            const updatedModel = templateModel.withUpdates({ customId: isNullOrWhitespace(customId) ? undefined : customId });
            setTemplateModel(updatedModel);
        },
        onStartDateRelativeValueChange: (value: string) => {
            const config: RelativeTemplateDateConfigModel = {
                relativeTo: templateModel.startDateRelativeConfig?.relativeTo,
                direction: templateModel.startDateRelativeConfig?.direction,
                value: isNullOrWhitespace(value) ? undefined : Number(value),
                unit: templateModel.startDateRelativeConfig?.unit,
            };
            setTemplateModel(
                templateModel.withUpdates({
                    startDateRelativeConfig: config,
                })
            );
        },
        onStartDateRelativeUnitChange: (unit: string | undefined) => {
            const config: RelativeTemplateDateConfigModel = {
                relativeTo: templateModel.startDateRelativeConfig?.relativeTo,
                direction: templateModel.startDateRelativeConfig?.direction,
                value: templateModel.startDateRelativeConfig?.value,
                unit: unit ? Number(unit) : undefined,
            };
            setTemplateModel(
                templateModel.withUpdates({
                    startDateRelativeConfig: config,
                })
            );
        },
        onStartDateRelativeDirectionChange: (direction: string | undefined) => {
            const config: RelativeTemplateDateConfigModel = {
                relativeTo: templateModel.startDateRelativeConfig?.relativeTo,
                direction: direction ? Number(direction) : undefined,
                value: templateModel.startDateRelativeConfig?.value,
                unit: templateModel.startDateRelativeConfig?.unit,
            };
            setTemplateModel(
                templateModel.withUpdates({
                    startDateRelativeConfig: config,
                })
            );
        },
        onStartDateRelativeToChange: (relativeTo: string | undefined) => {
            const config: RelativeTemplateDateConfigModel = {
                relativeTo: relativeTo ? Number(relativeTo) : undefined,
                direction: templateModel.startDateRelativeConfig?.direction,
                value: templateModel.startDateRelativeConfig?.value,
                unit: templateModel.startDateRelativeConfig?.unit,
            };
            setTemplateModel(
                templateModel.withUpdates({
                    startDateRelativeConfig: config,
                })
            );
        },
        onEndDateRelativeValueChange: (value: string) => {
            const config: RelativeTemplateDateConfigModel = {
                relativeTo: templateModel.endDateRelativeConfig?.relativeTo,
                direction: templateModel.endDateRelativeConfig?.direction,
                value: isNullOrWhitespace(value) ? undefined : Number(value),
                unit: templateModel.endDateRelativeConfig?.unit,
            };
            setTemplateModel(
                templateModel.withUpdates({
                    endDateRelativeConfig: config,
                })
            );
        },
        onEndDateRelativeUnitChange: (unit: string | undefined) => {
            const config: RelativeTemplateDateConfigModel = {
                relativeTo: templateModel.endDateRelativeConfig?.relativeTo,
                direction: templateModel.endDateRelativeConfig?.direction,
                value: templateModel.endDateRelativeConfig?.value,
                unit: unit ? Number(unit) : undefined,
            };
            setTemplateModel(
                templateModel.withUpdates({
                    endDateRelativeConfig: config,
                })
            );
        },
        onEndDateRelativeDirectionChange: (direction: string | undefined) => {
            const config: RelativeTemplateDateConfigModel = {
                relativeTo: templateModel.endDateRelativeConfig?.relativeTo,
                direction: direction ? Number(direction) : undefined,
                value: templateModel.endDateRelativeConfig?.value,
                unit: templateModel.endDateRelativeConfig?.unit,
            };
            setTemplateModel(
                templateModel.withUpdates({
                    endDateRelativeConfig: config,
                })
            );
        },
        onEndDateRelativeToChange: (relativeTo: string | undefined) => {
            const config: RelativeTemplateDateConfigModel = {
                relativeTo: relativeTo ? Number(relativeTo) : undefined,
                direction: templateModel.endDateRelativeConfig?.direction,
                value: templateModel.endDateRelativeConfig?.value,
                unit: templateModel.endDateRelativeConfig?.unit,
            };
            setTemplateModel(
                templateModel.withUpdates({
                    endDateRelativeConfig: config,
                })
            );
        },
        onEntityTemplatesChange: (entityTemplateIds: Array<string>) => {
            const existingTemplates = templateModel.entityTemplates.filter((ct) => entityTemplateIds.includes(ct.entityTemplateId));
            const newTemplateIds = entityTemplateIds.filter((id) => !templateModel.entityTemplates.some((ct) => ct.entityTemplateId === id));
            const newTemplates: Array<EntityTemplateSelection> = newTemplateIds
                .map((id) => {
                    const template = entityTemplatesQuery.data?.find((t) => t.id === id);
                    if (template) {
                        return {
                            entityTemplateId: id,
                            entityTemplateName: template.name,
                            isMandatory: false,
                        };
                    }
                    return null;
                })
                .filter((t): t is EntityTemplateSelection => t !== null);
            const updatedModel = templateModel.withUpdates({
                entityTemplates: [...existingTemplates, ...newTemplates],
            });
            setTemplateModel(updatedModel);
        },
        onEntityTemplateMandatoryChange: (entityTemplateId: string, isMandatory: boolean) => {
            const updatedModel = templateModel.withUpdates({
                entityTemplates: templateModel.entityTemplates.map((ct) => (ct.entityTemplateId === entityTemplateId ? { ...ct, isMandatory } : ct)),
            });
            setTemplateModel(updatedModel);
        },
    };

    const annualWheelActivityGeneralTypes = (hideObligation: boolean) => {
        return getEnumValues(AnnualWheelActivityGeneralType)
            .filter((x) => hideObligation && x != AnnualWheelActivityGeneralType.Obligation)
            .map((x) => {
                const name = translateAnnualWheelActivityGeneralTypeEnum(x.toString());
                return { name: name, id: x.toString() };
            });
    };

    return {
        onSave,
        showError,
        templateModel,
        modelUpdater,
        isEdit,
        complianceAreasLoading: complianceAreaQuery.isLoading,
        selectableComplianceAreas: complianceAreaQuery.data,
        annualWheelActivityGeneralTypes,
        isSaving: saveMutation.isLoading || updateMutation.isLoading,
        selectableEntityTemplates: entityTemplatesQuery.data,
        entityTemplatesLoading: entityTemplatesQuery.isLoading,
        selectableBusinessAreas: businessAreaQuery.data,
        businessAreasLoading: businessAreaQuery.isLoading,
    };

    function initiateTemplateModel(): AnnualWheelTemplateModel {
        return props.templateModel ? props.templateModel : AnnualWheelTemplateModel.createDefaultTemplate(!isTemplate);
    }

    function getSaveModel() {
        return {
            name: templateModel.name,
            description: templateModel.description,
            subTasks: templateModel.subTasks.map((x) => x.name),
            recurringInterval: templateModel.recurringInterval,
            notificationPeriodInDays: templateModel.notificationPeriodInDays,
            complianceAreaIds: templateModel.complianceAreaIds,
            generalType: templateModel.generalType,
            customId: templateModel.customId,
            recurringIntervalType: templateModel.recurringIntervalType,
            startDateRelativeConfig: templateModel.startDateRelativeConfig,
            endDateRelativeConfig: templateModel.endDateRelativeConfig,
            entityTemplates: templateModel.entityTemplates.map((ct) => ({
                entityTemplateId: ct.entityTemplateId,
                isMandatory: ct.isMandatory,
            })),
            businessAreaIds: templateModel.businessAreaIds,
            tagIds: templateModel.tagIds,
        };
    }
}
